import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { GrainOverlay } from '@/components/ui/GrainOverlay'

interface PageHeroProps {
  title: string
  /** Second line of the heading, shown in gold */
  accent?: string
  description?: ReactNode
  /** Photo shown in an arch frame beside the text */
  image?: string
  imageAlt?: string
  /** Tailwind object-position class for the photo crop */
  imagePosition?: string
  /** Custom visual (e.g. a book cover) shown instead of a photo */
  visual?: ReactNode
  /** Short key points shown as a row under the buttons */
  highlights?: string[]
  /** Curved edge blending into the next (light) section. Turn off when the next section is dark. */
  curve?: boolean
  /** Buttons or links shown under the description */
  children?: ReactNode
}

const delay = (ms: number) => ({ animationDelay: `${ms}ms` })

/** Line-art leaf that sits behind the hero content. */
const BotanicalLine = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 160 240"
    fill="none"
    aria-hidden="true"
    className={cn('pointer-events-none absolute text-green-700/40', className)}
  >
    <path
      d="M80 230 C80 230,16 160,16 96 C16 48,48 8,80 8 C112 8,144 48,144 96 C144 160,80 230,80 230Z"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <line
      x1="80"
      y1="8"
      x2="80"
      y2="230"
      stroke="currentColor"
      strokeWidth="1"
      strokeDasharray="4 5"
    />
    <path d="M80 65 C80 65,48 56,32 40" stroke="currentColor" strokeWidth="1" />
    <path d="M80 65 C80 65,112 56,128 40" stroke="currentColor" strokeWidth="1" />
    <path d="M80 110 C80 110,42 96,22 76" stroke="currentColor" strokeWidth="1" />
    <path d="M80 110 C80 110,118 96,138 76" stroke="currentColor" strokeWidth="1" />
    <path d="M80 158 C80 158,46 142,28 122" stroke="currentColor" strokeWidth="1" />
    <path d="M80 158 C80 158,114 142,132 122" stroke="currentColor" strokeWidth="1" />
  </svg>
)

/** The one hero used on every inner page — same family as the homepage hero. */
export const PageHero = ({
  title,
  accent,
  description,
  image,
  imageAlt = '',
  imagePosition = 'object-[50%_30%]',
  visual,
  highlights,
  curve = true,
  children,
}: PageHeroProps) => {
  const hasVisual = Boolean(image || visual)

  return (
    <section className="relative overflow-hidden bg-green-950 text-base-50">
      <GrainOverlay opacity={0.025} />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 -top-40 h-[560px] w-[560px] rounded-full bg-green-800/20 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-48 right-0 h-[420px] w-[420px] rounded-full bg-gold-400/10 blur-3xl"
      />
      <BotanicalLine className="-bottom-10 -left-10 hidden h-80 w-52 -rotate-12 sm:block" />
      {!hasVisual && (
        <BotanicalLine className="-right-6 top-1/2 hidden h-96 w-64 -translate-y-1/2 rotate-12 text-gold-300/25 lg:block" />
      )}

      <div
        className={cn(
          'relative z-10 mx-auto max-w-6xl px-6 py-14 sm:py-20 lg:py-24',
          hasVisual && 'grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16',
        )}
      >
        <div>
          <h1
            className="animate-fade-in-up font-display text-[clamp(2.4rem,6.5vw,4.25rem)] leading-[1.08] tracking-tight"
            style={delay(0)}
          >
            {title}
            {accent && <span className="mt-1 block text-gold-200">{accent}</span>}
          </h1>
          {description && (
            <div
              className="animate-fade-in-up mt-6 max-w-xl space-y-4 text-base leading-relaxed text-base-100 sm:text-lg"
              style={delay(120)}
            >
              {description}
            </div>
          )}
          {children && (
            <div
              className="animate-fade-in-up mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6"
              style={delay(200)}
            >
              {children}
            </div>
          )}
          {highlights && highlights.length > 0 && (
            <ul
              className="animate-fade-in-up mt-9 flex flex-wrap gap-x-6 gap-y-3 border-t border-white/15 pt-6 text-sm text-green-100"
              style={delay(280)}
            >
              {highlights.map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-gold-300" />
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>

        {hasVisual && (
          <div
            className="animate-slide-in-right relative mx-auto w-full max-w-sm lg:max-w-none"
            style={delay(120)}
          >
            {/* Gold rings behind the frame */}
            <div
              aria-hidden="true"
              className="absolute -right-5 -top-5 h-28 w-28 rounded-full border border-gold-300/40"
            />
            <div
              aria-hidden="true"
              className="absolute -right-2 -top-2 h-28 w-28 rounded-full border border-gold-300/20"
            />

            {visual ? (
              <div className="relative flex justify-center py-4">{visual}</div>
            ) : (
              <figure className="relative overflow-hidden rounded-t-[7rem] rounded-b-2xl border border-white/10 bg-green-900 shadow-xl lg:rounded-t-[9rem]">
                <img
                  src={image}
                  alt={imageAlt}
                  loading="eager"
                  className={cn(
                    'aspect-[4/3] w-full object-cover sm:aspect-[5/4] lg:aspect-[4/5]',
                    imagePosition,
                  )}
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-green-950/60 to-transparent"
                />
              </figure>
            )}
          </div>
        )}
      </div>

      {curve && (
        <svg
          viewBox="0 0 1440 48"
          preserveAspectRatio="none"
          aria-hidden="true"
          className="absolute inset-x-0 -bottom-px z-10 h-6 w-full text-base-50 sm:h-10"
        >
          <path d="M0 48V30Q720 -10 1440 30V48Z" fill="currentColor" />
        </svg>
      )}
    </section>
  )
}

/** Filled pill button for use on the dark hero. */
export const heroPrimaryLink =
  'inline-flex min-h-12 items-center justify-center gap-3 rounded-full bg-base-50 px-6 py-3.5 text-sm font-semibold text-green-950 transition-colors hover:bg-gold-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-200 focus-visible:ring-offset-4 focus-visible:ring-offset-green-950'

/** Quiet underlined link for use on the dark hero. */
export const heroSecondaryLink =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-sm text-sm text-base-100 underline decoration-gold-200/50 underline-offset-8 transition-colors hover:text-gold-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-200'
